import React, { useEffect, useState, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { getBusinessDashboard, refreshBusinessDashboardCache, downloadBusinessDashboard } from '../../Redux/actions';
import {
    Box, Paper, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, CircularProgress, Chip, Grid,
    Pagination, IconButton
} from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import RefreshIcon from '@mui/icons-material/Refresh';
import DownloadIcon from '@mui/icons-material/Download';
import { Tooltip } from '@mui/material';
import { exportBusinessDashboardToExcel } from '../../utils/Common';
import { paginationStyle } from '../../theme/formStyles';
import { updateParams } from '../../utils';
import SearchBox from '../Common/SearchBox';
import Filter from '../Common/Filter';

const formatCurrency = (value) => {
    if (!value && value !== 0) return '-';
    return new Intl.NumberFormat('en-IN', {
        style: 'currency',
        currency: 'INR',
        maximumFractionDigits: 0,
    }).format(value);
};

const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    try {
        const d = new Date(dateStr);
        return d.toLocaleDateString('en-IN', { day: '2-digit', month: '2-digit', year: 'numeric' });
    } catch {
        return dateStr;
    }
};

const StatusChip = ({ status }) => {
    const colorMap = {
        'open': '#2196f3',
        'partially_invoiced': '#ff9800',
        'invoiced': '#4caf50',
        'closed': '#9e9e9e',
        'void': '#f44336',
        'overdue': '#f44336',
        'draft': '#607d8b',
    };
    const color = colorMap[status?.toLowerCase()] || '#9e9e9e';
    return (
        <Chip
            label={status?.replace(/_/g, ' ') || '-'}
            size="small"
            sx={{
                backgroundColor: color + '20',
                color: color,
                fontWeight: 600,
                fontSize: '11px',
                textTransform: 'capitalize',
            }}
        />
    );
};

const itemsPerPage = 10;

// Sticky column widths and cumulative left offsets
const stickyWidths = [100, 130, 150, 120, 120]; // Segment, Client Group, Facility, Owner BD/Project, SO/PO No
const stickyLefts = stickyWidths.map((_, i) => stickyWidths.slice(0, i).reduce((a, b) => a + b, 0));

const stickyStyle = (colIndex, extra = {}) => ({
    position: 'sticky',
    left: stickyLefts[colIndex],
    minWidth: stickyWidths[colIndex],
    maxWidth: stickyWidths[colIndex],
    backgroundColor: extra.backgroundColor || '#fff',
    zIndex: 2,
    wordBreak: 'break-word',
    ...extra,
});

const stickyHeadStyle = (colIndex) => ({
    position: 'sticky',
    left: stickyLefts[colIndex],
    minWidth: stickyWidths[colIndex],
    maxWidth: stickyWidths[colIndex],
    backgroundColor: '#fff',
    zIndex: 4,
    fontSize: '14px',
    fontWeight: 'bold',
    borderBottom: '1px solid #333',
});

export default function BusinessDashboard() {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [queryParams] = useSearchParams();
    const existingParams = Object.fromEntries([...queryParams]);

    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [totalPages, setTotalPages] = useState(1);
    const [totalClients, setTotalClients] = useState(0);
    const [totalFacilities, setTotalFacilities] = useState(0);
    const [totalSalesOrders, setTotalSalesOrders] = useState(0);
    const [showFilterDialog, setShowFilterDialog] = useState(false);
    const [refreshing, setRefreshing] = useState(false);
    const [downloading, setDownloading] = useState(false);
    const [showScrollIndicator, setShowScrollIndicator] = useState(false);
    const tableContainerRef = useRef(null);
    const [isSearchOrFilterApplied, setIsSearchOrFilterApplied] = useState(false);
    const [bdFilterOptions, setBdFilterOptions] = useState({});

    const currentPage = parseInt(existingParams?.page) || 1;

    useEffect(() => {
        if (!existingParams?.page) {
            navigate({ search: 'page=1' }, { replace: true });
            return;
        }
        fetchDashboard();
    }, [queryParams]);

    const fetchDashboard = () => {
        setLoading(true);
        const params = {
            page: isSearchOrFilterApplied ? 1 : (parseInt(existingParams?.page) || 1),
            per_page: itemsPerPage,
            search: existingParams?.search || '',
            segment: existingParams?.segment || '',
            client_group: existingParams?.client_group || '',
            facility: existingParams?.facility || '',
            status: existingParams?.status || '',
            contract_type: existingParams?.contract_type || '',
            billing_cycle: existingParams?.billing_cycle || '',
        };
        setIsSearchOrFilterApplied(false);
        dispatch(getBusinessDashboard(params)).then((resp) => {
            if (resp?.data?.success) {
                setData(resp.data.data || []);
                setTotalPages(resp.data.totalPages || 1);
                setTotalClients(resp.data.totalClients || 0);
                setTotalFacilities(resp.data.totalFacilities || 0);
                setTotalSalesOrders(resp.data.totalSalesOrders || 0);
                if (resp.data.filterOptions) {
                    setBdFilterOptions(resp.data.filterOptions);
                }
            }
            setLoading(false);
        }).catch(() => {
            setLoading(false);
        });
    };

    const updateQueryParams = (params) => {
        const searchParams = updateParams(existingParams, params);
        navigate({ search: searchParams.toString() }, { replace: true });
    };

    const handleSearch = (value) => {
        updateQueryParams({ search: value, page: 1 });
        setIsSearchOrFilterApplied(true);
    };

    const handlePageChange = (event, newPage) => {
        updateQueryParams({ page: newPage });
    };

    const handleTabSwitch = () => {
        setIsSearchOrFilterApplied(true);
    };

    const handleRefreshCache = () => {
        setRefreshing(true);
        dispatch(refreshBusinessDashboardCache()).then((resp) => {
            if (resp?.data?.success) {
                fetchDashboard();
            }
            setRefreshing(false);
        }).catch(() => {
            setRefreshing(false);
        });
    };

    const checkScroll = () => {
        const container = tableContainerRef.current;
        if (container) {
            const hasScroll = container.scrollWidth > container.clientWidth;
            const isScrolled = container.scrollLeft > 0;
            setShowScrollIndicator(hasScroll && isScrolled);
        }
    };

    useEffect(() => {
        checkScroll();
        window.addEventListener('resize', checkScroll);
        return () => window.removeEventListener('resize', checkScroll);
    }, [data]);

    const handleDownload = () => {
        setDownloading(true);
        const params = {
            segment: existingParams?.segment || '',
            client_group: existingParams?.client_group || '',
            facility: existingParams?.facility || '',
            status: existingParams?.status || '',
            contract_type: existingParams?.contract_type || '',
            billing_cycle: existingParams?.billing_cycle || '',
        };
        dispatch(downloadBusinessDashboard(params)).then((resp) => {
            if (resp?.data?.success) {
                exportBusinessDashboardToExcel(resp.data.data);
            }
            setDownloading(false);
        }).catch(() => {
            setDownloading(false);
        });
    };

    // Group data by facility for rowSpan rendering
    const groupedByFacility = {};
    data.forEach(row => {
        const key = `${row.segment}||${row.client_group}||${row.facility_name}`;
        if (!groupedByFacility[key]) {
            groupedByFacility[key] = {
                segment: row.segment,
                client_group: row.client_group,
                facility_name: row.facility_name,
                owner: row.owner || [],
                orders: [],
            };
        }
        groupedByFacility[key].orders.push(row);
    });

    const facilityGroups = Object.values(groupedByFacility);

    // Compute facility-level totals
    const computeFacilityTotals = (orders) => ({
        totalContractValue: orders.reduce((sum, o) => sum + (o.contract_value || 0), 0),
        totalInvoiced: orders.reduce((sum, o) => sum + (o.total_invoiced_amount || 0), 0),
        totalOutstanding: orders.reduce((sum, o) => sum + (o.outstanding || 0), 0),
    });

    const stickyHeaders = ['Segment', 'Client Group', 'Facility', 'Owner BD/Project', 'SO/PO No'];
    const scrollHeaders = [
        'Status', 'Contract Type', 'Billing Cycle', 'PO Date',
        'Contract Value', 'Total Contract Value',
        'Invoiced Date', 'Invoiced', 'Total Invoiced',
        'PO to Invoice Date', 'Outstanding ₹', 'Aging (Days)',
        'Total Outstanding', 'Next Invoice Date'
    ];

    let tableContent = null;

    if (loading) {
        tableContent = (
            <TableRow>
                <TableCell colSpan={stickyHeaders.length + scrollHeaders.length} align="center">
                    <CircularProgress sx={{ color: '#008080' }} />
                </TableCell>
            </TableRow>
        );
    } else if (facilityGroups.length === 0) {
        tableContent = (
            <TableRow>
                <TableCell colSpan={stickyHeaders.length + scrollHeaders.length} align="left">
                    <h5 className="w3-text-grey">No Data Found</h5>
                </TableCell>
            </TableRow>
        );
    } else {
        tableContent = facilityGroups.map((facility, facilityIndex) => {
            const orders = facility.orders;
            const totals = computeFacilityTotals(orders);
            const isLastFacility = facilityIndex === facilityGroups.length - 1;

            return orders.map((row, index) => {
                const isLast = index === orders.length - 1;
                // Determine if we're at a client group boundary
                const nextFacility = facilityIndex < facilityGroups.length - 1 ? facilityGroups[facilityIndex + 1] : null;
                const isClientChange = nextFacility && nextFacility.client_group !== facility.client_group;

                // Border on merged cells (Segment, Client Group, Facility, Owner) only when client changes
                const mergedBorder = (!isLastFacility && isClientChange) ? '2px solid #bdbdbd' : 'none';

                // Border from SO/PO No column onwards: thicker on client change, thin between facilities of same client
                const soBorder = (!isLastFacility) ? (isClientChange ? '2px solid #bdbdbd' : '1px solid #e0e0e0') : 'none';

                // Inner rows (between SOs within same facility): only apply soBorder on the last SO row
                const innerBorder = isLast ? soBorder : 'none';

                return (
                    <TableRow
                        key={`${facility.facility_name}-${index}`}
                        sx={{}}
                    >
                        {/* Sticky merged cells: Segment, Client Group, Facility, Owner */}
                        {index === 0 && (
                            <>
                                <TableCell style={{ ...stickyStyle(0), borderBottom: mergedBorder, fontWeight: 500 }} rowSpan={orders.length}>
                                    {facility.segment || '-'}
                                </TableCell>
                                <TableCell style={{ ...stickyStyle(1), borderBottom: mergedBorder, fontWeight: 500 }} rowSpan={orders.length}>
                                    {facility.client_group || '-'}
                                </TableCell>
                                <TableCell style={{ ...stickyStyle(2), borderBottom: mergedBorder, fontWeight: 500 }} rowSpan={orders.length}>
                                    {facility.facility_name || '-'}
                                </TableCell>
                                <TableCell style={{ ...stickyStyle(3), borderBottom: mergedBorder, fontWeight: 500 }} rowSpan={orders.length}>
                                    {facility.owner?.length > 0 ? facility.owner.map(o => o.name || o).join(', ') : '-'}
                                </TableCell>
                            </>
                        )}

                        {/* Sticky SO-specific: SO/PO No */}
                        <TableCell style={{ ...stickyStyle(4), borderBottom: innerBorder }}>{row.so_po_no || '-'}</TableCell>

                        {/* Scrollable columns */}
                        <TableCell style={{ borderBottom: innerBorder }}><StatusChip status={row.status} /></TableCell>
                        <TableCell style={{ borderBottom: innerBorder }}>{row.contract_type || '-'}</TableCell>
                        <TableCell style={{ borderBottom: innerBorder }}>{row.billing_cycle || '-'}</TableCell>
                        <TableCell style={{ borderBottom: innerBorder }}>{formatDate(row.po_date)}</TableCell>
                        <TableCell style={{ borderBottom: innerBorder }}>{formatCurrency(row.contract_value)}</TableCell>

                        {/* Merged: Total Contract Value */}
                        {index === 0 && (
                            <TableCell style={{ borderBottom: soBorder, fontWeight: 600, color: '#00695c' }} rowSpan={orders.length}>
                                {formatCurrency(totals.totalContractValue)}
                            </TableCell>
                        )}

                        <TableCell style={{ borderBottom: innerBorder }}>{formatDate(row.last_invoiced_date)}</TableCell>
                        <TableCell style={{ borderBottom: innerBorder }}>{formatCurrency(row.total_invoiced_amount)}</TableCell>

                        {/* Merged: Total Invoiced */}
                        {index === 0 && (
                            <TableCell style={{ borderBottom: soBorder, fontWeight: 600, color: '#00695c' }} rowSpan={orders.length}>
                                {formatCurrency(totals.totalInvoiced)}
                            </TableCell>
                        )}

                        <TableCell style={{ borderBottom: innerBorder }}>{row.po_to_invoice_days !== null ? `${row.po_to_invoice_days} days` : '-'}</TableCell>
                        <TableCell style={{ borderBottom: innerBorder }}>{formatCurrency(row.outstanding)}</TableCell>
                        <TableCell style={{ borderBottom: innerBorder }}>
                            <span style={{ color: row.aging_days > 90 ? '#f44336' : row.aging_days > 30 ? '#ff9800' : '#333' }}>
                                {row.aging_days || 0}
                            </span>
                        </TableCell>

                        {/* Merged: Total Outstanding */}
                        {index === 0 && (
                            <TableCell style={{ borderBottom: soBorder, fontWeight: 600, color: totals.totalOutstanding > 0 ? '#e53935' : '#00695c' }} rowSpan={orders.length}>
                                {formatCurrency(totals.totalOutstanding)}
                            </TableCell>
                        )}

                        <TableCell style={{ borderBottom: innerBorder }}>{formatDate(row.next_invoice_date)}</TableCell>
                    </TableRow>
                );
            });
        });
    }

    return (
        <div className='w3-padding-large'>
            <Grid container>
                <Grid item md={12}>
                    {/* Summary stats + Search + Filter */}
                    <Grid container spacing={2} paddingY={2} justifyContent="space-between" alignItems="center">
                        <Grid item container md={6} spacing={2} alignItems="center">
                            <Grid item>
                                <Box sx={{ fontWeight: 600, color: 'black', backgroundColor: 'white', padding: '10px', borderRadius: '4px' }}>
                                    Total Clients: &nbsp;
                                    <span style={{ fontWeight: 600, color: '#e68a00', fontSize: '18px' }}>
                                        {loading ? <CircularProgress size={20} color="primary" /> : totalClients}
                                    </span>
                                </Box>
                            </Grid>
                            <Grid item>
                                <Box sx={{ fontWeight: 600, color: 'black', backgroundColor: 'white', padding: '10px', borderRadius: '4px' }}>
                                    Total Facilities: &nbsp;
                                    <span style={{ fontWeight: 600, color: '#e68a00', fontSize: '18px' }}>
                                        {loading ? <CircularProgress size={20} color="primary" /> : totalFacilities}
                                    </span>
                                </Box>
                            </Grid>
                            <Grid item>
                                <Box sx={{ fontWeight: 600, color: 'black', backgroundColor: 'white', padding: '10px', borderRadius: '4px' }}>
                                    Total Sales Orders: &nbsp;
                                    <span style={{ fontWeight: 600, color: '#e68a00', fontSize: '18px' }}>
                                        {loading ? <CircularProgress size={20} color="primary" /> : totalSalesOrders}
                                    </span>
                                </Box>
                            </Grid>
                        </Grid>
                        <Grid item container md={6} justifyContent="flex-end" alignItems="center">
                            <Grid item>
                                <h6 style={{ fontSize: '15px', paddingRight: '5px', margin: 0, whiteSpace: 'nowrap' }}>Search By:</h6>
                            </Grid>
                            <Grid item>
                                <SearchBox
                                    value={queryParams.get('search') || ''}
                                    placeholder='SO/PO Number'
                                    search={handleSearch}
                                />
                            </Grid>
                            <Grid item>
                                <IconButton
                                    onClick={() => setShowFilterDialog(true)}
                                    title="Apply Filter">
                                    <FilterListIcon />
                                </IconButton>
                            </Grid>
                            <Grid item>
                                <Tooltip title="Refresh data from Zoho">
                                    <span>
                                        <IconButton
                                            onClick={handleRefreshCache}
                                            disabled={refreshing}
                                        >
                                            {refreshing
                                                ? <CircularProgress size={20} sx={{ color: '#008080' }} />
                                                : <RefreshIcon />}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Grid>
                            <Grid item>
                                <Tooltip title="Download">
                                    <span>
                                        <IconButton
                                            onClick={handleDownload}
                                            disabled={downloading}
                                        >
                                            {downloading
                                                ? <CircularProgress size={20} sx={{ color: '#008080' }} />
                                                : <DownloadIcon />}
                                        </IconButton>
                                    </span>
                                </Tooltip>
                            </Grid>
                        </Grid>
                    </Grid>

                    <Filter
                        filterArea="BusinessDashboard"
                        showFilterDialog={showFilterDialog}
                        setShowFilterDialog={setShowFilterDialog}
                        updateQueryParams={updateQueryParams}
                        queryParams={queryParams}
                        setIsSearchOrFilterApplied={setIsSearchOrFilterApplied}
                        clearFilters={false}
                        handleTabSwitch={handleTabSwitch}
                        bdFilterOptions={bdFilterOptions}
                    />

                    {/* Table */}
                    <Grid paddingX={0} paddingY={1} sx={{ position: 'relative' }}>
                        {showScrollIndicator && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    left: `${stickyWidths.reduce((a, b) => a + b, 0)}px`, // End of Status column
                                    top: '8px',
                                    bottom: '8px',
                                    width: '30px',
                                    background: 'linear-gradient(to right, rgba(0,0,0,0.2), transparent)',
                                    pointerEvents: 'none',
                                    zIndex: 100,
                                }}
                            />
                        )}
                        <TableContainer
                            component={Paper}
                            ref={tableContainerRef}
                            onScroll={checkScroll}
                            sx={{
                                overflowX: 'auto',
                                position: 'relative'
                            }}
                        >
                            <Table aria-label="business dashboard table" style={{ tableLayout: 'auto', borderCollapse: 'separate', borderSpacing: 0 }}>
                                <TableHead>
                                    <TableRow>
                                        {stickyHeaders.map((col, idx) => (
                                            <TableCell key={idx} style={stickyHeadStyle(idx)}>{col}</TableCell>
                                        ))}
                                        {scrollHeaders.map((col, idx) => (
                                            <TableCell key={idx + 6} style={{ fontSize: '14px', fontWeight: 'bold', borderBottom: '1px solid #333' }}>{col}</TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {tableContent}
                                </TableBody>
                            </Table>
                        </TableContainer>
                    </Grid>

                    {/* Pagination */}
                    {(!loading && facilityGroups.length > 0) && (
                        <Grid container item xs={12} style={{ display: 'flex', justifyContent: 'center', paddingTop: '16px' }}>
                            <Grid item xs={12} style={{ display: 'flex', justifyContent: 'flex-end', paddingRight: '24px' }}>
                                <span style={{ fontFamily: '"Helvetica Neue"', fontSize: '13px', color: '#3d3d3d' }}>
                                    {Math.min((currentPage - 1) * itemsPerPage + 1, totalFacilities)}-
                                    {Math.min(currentPage * itemsPerPage, totalFacilities)} of {totalFacilities} facilities
                                </span>
                            </Grid>
                            <Pagination
                                page={currentPage}
                                count={totalPages}
                                variant="outlined"
                                shape="rounded"
                                sx={paginationStyle}
                                onChange={handlePageChange}
                            />
                        </Grid>
                    )}
                </Grid>
            </Grid>
        </div>
    );
}
